<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import logoUrl from '@/assets/logo.svg'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import UiRow from '@/components/UiRow.vue'
import { useBotUsername, useLegalLinks } from '@/composables/useBotUsername'
import { useBrowserLogin } from '@/composables/useBrowserLogin'
import { isInsideTelegram } from '@/telegramWebApp'

const { t } = useI18n()
const botUsername = useBotUsername()
const { privacyUrl, imprintUrl } = useLegalLinks()
const faqKeys = ['1', '2', '3', '4'] as const
const { loginConfig, loginConfigState, loginReady, loginBusy, loginMessage, prepare, login } = useBrowserLogin()

const botUrl = computed(() => (botUsername.value ? `https://t.me/${botUsername.value}` : ''))

onMounted(() => {
  if (!isInsideTelegram()) void prepare()
})
</script>

<template>
  <div class="landing">
    <img class="logo" :src="logoUrl" alt="" width="96" height="96" />
    <h1>{{ t('landing.title') }}</h1>
    <p class="pitch">{{ t('landing.pitch') }}</p>

    <div class="actions">
      <button
        v-if="loginConfig"
        type="button"
        class="primary"
        :disabled="!loginReady || loginBusy"
        @click="login"
      >
        {{ t('auth.loginWithTelegram') }}
      </button>
      <p v-if="loginConfigState === 'off'" class="hint" data-testid="login-off">{{ t('auth.loginNotSetUp') }}</p>
      <p v-else-if="loginConfigState === 'failed'" class="hint" data-testid="login-failed">
        {{ t('auth.loginConfigFailed') }}
      </p>
      <p v-if="loginMessage" class="hint">{{ loginMessage }}</p>
      <a v-if="botUrl" class="secondary" :href="botUrl" target="_blank" rel="noopener">{{ t('landing.openBot') }}</a>
    </div>

    <UiGroup>
      <UiList>
        <UiRow>{{ t('landing.feature1') }}</UiRow>
        <UiRow>{{ t('landing.feature2') }}</UiRow>
        <UiRow>{{ t('landing.feature3') }}</UiRow>
      </UiList>
    </UiGroup>

    <UiGroup :title="t('landing.faq.title')">
      <UiList>
        <UiRow v-for="n in faqKeys" :key="n" as="div">
          <details class="faq" :data-testid="`faq-${n}`">
            <summary>{{ t(`landing.faq.q${n}`) }}</summary>
            <p>{{ t(`landing.faq.a${n}`) }}</p>
          </details>
        </UiRow>
      </UiList>
    </UiGroup>

    <footer v-if="privacyUrl || imprintUrl" class="legal" data-testid="legal">
      <a v-if="privacyUrl" :href="privacyUrl" target="_blank" rel="noopener">{{ t('landing.privacy') }}</a>
      <a v-if="imprintUrl" :href="imprintUrl" target="_blank" rel="noopener">{{ t('landing.imprint') }}</a>
    </footer>
  </div>
</template>

<style scoped>
.landing {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 28rem;
  margin: 0 auto;
  padding: 2rem 0;
  text-align: center;
}

.landing > :deep([data-ui-group]) {
  align-self: stretch;
  text-align: left;
}

.logo {
  border-radius: 22%;
}

h1 {
  margin: 1rem 0 0.25rem;
}

.pitch {
  margin: 0 0 1.5rem;
  color: var(--tg-hint-color);
}

.actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.hint {
  margin: 0;
  color: var(--tg-hint-color);
}

.primary {
  padding: 0.6rem 1.25rem;
  border-radius: 0.5rem;
  border: 1px solid var(--tg-button-color);
  background: var(--tg-button-color);
  color: var(--tg-button-text-color);
  font: inherit;
  cursor: pointer;
}

.primary:disabled {
  opacity: 0.6;
  cursor: default;
}

.secondary {
  color: var(--tg-link-color);
}

.faq {
  min-width: 0;
  overflow-wrap: anywhere;
}

.faq summary {
  cursor: pointer;
}

.faq p {
  margin: 0.5rem 0 0;
  color: var(--tg-hint-color);
}

.legal {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem 1.25rem;
}

.legal a {
  color: var(--tg-link-color);
}
</style>
